import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  User,
  Building2,
  BarChart3,
  FileQuestion,
  CheckCircle2,
  Loader2,
  Check,
  X,
  MessageSquare,
} from 'lucide-react';
import { onboardingAPI, suppliersAPI, FilledDataset, SupplierProfile } from '../../lib/api';
import { toast } from 'sonner';
import ApprovalModals from './wizard/ApprovalModals';
import ApprovalQuestionnaireCards from './wizard/ApprovalQuestionnaireCards';
import { useFormatters } from '../../contexts/PreferencesContext';

// Wizard step configuration
interface WizardStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  isDynamic?: boolean;
  datasetId?: number;
  completedCount?: number;
  totalCount?: number;
}

export default function SupplierApprovalWizard() {
  const { id } = useParams();
  const { formatDateTime } = useFormatters();
  const navigate = useNavigate();
  const supplierId = Number(id);

  // Approval status type
  interface ApprovalLevel {
    level: number;
    decision: string;
    reviewer: string | null;
    decided_at: string | null;
    comments: string;
  }

  interface ApprovalStatus {
    total_levels: number;
    current_level: number | null;
    completed_levels: number;
    levels: ApprovalLevel[];
    is_complete: boolean;
    is_rejected: boolean;
  }

  // State
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState<SupplierProfile | null>(null);
  const [questionnaires, setQuestionnaires] = useState<FilledDataset[]>([]);
  const [approvalStatuses, setApprovalStatuses] = useState<Record<number, ApprovalStatus>>({});
  const [spendScore, setSpendScore] = useState<number | null>(null);
  const [riskScore, setRiskScore] = useState<number | null>(null);
  const [_currentStep, _setCurrentStep] = useState(1);
  const [_steps, setSteps] = useState<WizardStep[]>([]);

  // Modal states
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [moreInfoRequired, setMoreInfoRequired] = useState('');
  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState<number[]>([]);
  const [approvalLevel, setApprovalLevel] = useState<'1' | '2'>('1');
  const [approvalComment, setApprovalComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (supplierId) {
      fetchData();
    }
  }, [supplierId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch supplier info and their questionnaires in parallel
      const [supplierRes, questionnairesRes] = await Promise.all([
        suppliersAPI.getProfile(supplierId),
        onboardingAPI.getFilledDatasets({ supplier: supplierId }),
      ]);

      setSupplier(supplierRes.data);
      const filledDatasets = questionnairesRes.data.results || questionnairesRes.data || [];
      setQuestionnaires(filledDatasets);

      // CR-4054: Fetch onboarding workflow to get spend/risk scores
      try {
        const workflowsRes = await onboardingAPI.getWorkflows({ supplier: supplierId });
        const workflows = workflowsRes.data.results || workflowsRes.data || [];
        if (workflows.length > 0) {
          const workflow = workflows[0];
          setSpendScore(workflow.spend_score ?? null);
          setRiskScore(workflow.calculated_risk_score ?? null);
        }
      } catch {
        // Best effort - scores may not be available
      }

      // Fetch approval statuses for questionnaires that are submitted/under review
      const statusPromises = filledDatasets
        .filter((d: FilledDataset) => ['submitted', 'under_review', 'approved', 'rejected', 'more_info_required'].includes(d.status))
        .map(async (d: FilledDataset) => {
          try {
            const statusRes = await onboardingAPI.getApprovalStatus(d.id);
            return { id: d.id, status: statusRes.data };
          } catch {
            return null;
          }
        });

      const statuses = await Promise.all(statusPromises);
      const statusMap: Record<number, ApprovalStatus> = {};
      statuses.forEach((s) => {
        if (s) statusMap[s.id] = s.status;
      });
      setApprovalStatuses(statusMap);

      // Build wizard steps
      buildWizardSteps(filledDatasets);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildWizardSteps = (datasets: FilledDataset[]) => {
    const wizardSteps: WizardStep[] = [
      {
        id: 'user_info',
        title: 'Your Details',
        description: 'Setup your account details',
        icon: <User className="h-6 w-6" />,
      },
      {
        id: 'company_info',
        title: 'Organisation Details',
        description: 'Setup your organisation details',
        icon: <Building2 className="h-6 w-6" />,
      },
    ];

    // Add Risk Questions step if applicable (for Due Diligence status)
    const hasRiskQuestions = datasets.some(d => d.dataset_type === 'risk');
    if (hasRiskQuestions) {
      const riskDataset = datasets.find(d => d.dataset_type === 'risk');
      wizardSteps.push({
        id: 'risk_questions',
        title: 'Risk Questions',
        description: 'Risk level score',
        icon: <BarChart3 className="h-6 w-6" />,
        completedCount: riskDataset?.questions_answered || 0,
        totalCount: riskDataset?.questions_total || 0,
      });
    }

    // Add dynamic questionnaire steps
    datasets.filter(d => d.dataset_type !== 'risk').forEach((dataset) => {
      wizardSteps.push({
        id: `questionnaire_${dataset.id}`,
        title: dataset.dataset_name || 'Questionnaire',
        description: `${dataset.questions_answered}/${dataset.questions_total} - ${Math.round(dataset.completion_percentage)}%`,
        icon: <FileQuestion className="h-6 w-6" />,
        isDynamic: true,
        datasetId: dataset.id,
        completedCount: dataset.questions_answered,
        totalCount: dataset.questions_total,
      });
    });

    // Add Approval Review step
    wizardSteps.push({
      id: 'approval_review',
      title: 'Approval Review',
      description: '',
      icon: <CheckCircle2 className="h-6 w-6" />,
    });

    setSteps(wizardSteps);
  };


  const handleApprove = async () => {
    setActionLoading(true);
    try {
      // Approve all submitted questionnaires
      for (const q of questionnaires.filter(q => q.status === 'submitted' || q.status === 'under_review')) {
        await onboardingAPI.approveFilledDataset(q.id, approvalComment);
      }
      setShowApproveModal(false);
      toast.success('Questionnaires approved.');
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve questionnaires.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return;
    setActionLoading(true);
    try {
      // Decline all submitted questionnaires
      for (const q of questionnaires.filter(q => q.status === 'submitted' || q.status === 'under_review')) {
        await onboardingAPI.rejectFilledDataset(q.id, declineReason);
      }
      setShowDeclineModal(false);
      setDeclineReason('');
      toast.success('Questionnaires declined.');
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to decline:', error);
      toast.error('Failed to decline questionnaires.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestMoreInfo = async () => {
    if (!moreInfoRequired.trim() || selectedQuestionnaires.length === 0) return;
    setActionLoading(true);
    try {
      // Request more info for selected questionnaires
      for (const qId of selectedQuestionnaires) {
        await onboardingAPI.requestMoreInfo(qId, moreInfoRequired);
      }
      setShowMoreInfoModal(false);
      setMoreInfoRequired('');
      setSelectedQuestionnaires([]);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Failed to request more info:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const supplierOnboardingStatus = (supplier as unknown as Record<string, unknown>)?.['onboarding_status'] as string | undefined;
  const hasPendingApprovals = supplierOnboardingStatus === 'pending_initial'
    || supplierOnboardingStatus === 'due_diligence'
    || questionnaires.some(q => q.status === 'submitted' || q.status === 'under_review');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Extract contact info from wizard_draft if available, fallback to supplier record
  const wizardDraft = (supplier as unknown as Record<string, unknown>)?.['wizard_draft'] as Record<string, unknown> | undefined;
  const draftFormData = wizardDraft?.['form_data'] as Record<string, unknown> | undefined;
  const contactEmail = (draftFormData?.['contact_email'] as string) || (supplier as unknown as Record<string, unknown>)?.['contact_email'] as string || supplier?.company_details?.primary_email || '';
  const contactPhone = (draftFormData?.['contact_phone'] as string) || (supplier as unknown as Record<string, unknown>)?.['contact_phone'] as string || supplier?.company_details?.primary_phone || '';
  const contactName = [
    (draftFormData?.['contact_first_name'] as string) || '',
    (draftFormData?.['contact_last_name'] as string) || '',
  ].filter(Boolean).join(' ');
  const contactJobTitle = (draftFormData?.['contact_job_title'] as string) || (supplier as unknown as Record<string, unknown>)?.['contact_job_title'] as string || '';
  const companyAddress = (draftFormData?.['company_address'] as string) || supplier?.company_details?.address_line_1 || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/suppliers')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{supplier?.company_name}</h1>
          {supplierOnboardingStatus === 'pending_initial' && (
            <Badge className="bg-amber-100 text-amber-800">PENDING APPROVAL</Badge>
          )}
          {supplierOnboardingStatus === 'due_diligence' && (
            <Badge className="bg-indigo-100 text-indigo-800">DUE DILIGENCE</Badge>
          )}
          {riskScore !== null && (
            <Badge className={
              riskScore > 60 ? 'bg-red-100 text-red-800' :
              riskScore > 30 ? 'bg-amber-100 text-amber-800' :
              'bg-green-100 text-green-800'
            }>
              {riskScore > 60 ? 'HIGH' : riskScore > 30 ? 'MEDIUM' : 'LOW'}
            </Badge>
          )}
        </div>
      </div>

      <p className="text-muted-foreground">
        Review supplier details and risk assessment before approving or rejecting.
      </p>

      {/* Company Details - matching onboarding review modal layout */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground tracking-wider mb-3">COMPANY DETAILS</h4>
        <div className="grid grid-cols-3 gap-px bg-gray-200 rounded-lg overflow-hidden">
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Company Name</p>
            <p className="font-medium mt-1">{supplier?.company_name || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="font-medium mt-1">{contactEmail || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Phone</p>
            <p className="font-medium mt-1">{contactPhone || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Contact Name</p>
            <p className="font-medium mt-1">{contactName || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Address</p>
            <p className="font-medium mt-1">{companyAddress || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Job Title</p>
            <p className="font-medium mt-1">{contactJobTitle || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Est. Annual Spend</p>
            <p className="font-medium mt-1">{supplier?.annual_revenue ? `£${Number(supplier.annual_revenue).toLocaleString()}` : (draftFormData?.['annual_spend'] as string || 'Not provided')}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Employee Count</p>
            <p className="font-medium mt-1">{supplier?.employee_count || 'Not provided'}</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-xs text-muted-foreground">Submitted At</p>
            <p className="font-medium mt-1">{supplier?.created_at ? formatDateTime(supplier.created_at) : 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Risk Assessment Answers - from workflow config */}
      {spendScore !== null || riskScore !== null ? (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground tracking-wider mb-3">RISK & SPEND ASSESSMENT</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center border">
              <p className="text-sm text-muted-foreground mb-1">Spend Score</p>
              <p className="text-3xl font-bold text-blue-600">{spendScore !== null ? `${spendScore}/10` : 'N/A'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center border">
              <p className="text-sm text-muted-foreground mb-1">Risk Score</p>
              <p className="text-3xl font-bold text-orange-600">
                {riskScore !== null ? `${Math.round(riskScore)}%` :
                  questionnaires.filter(q => q.dataset_type === 'risk').some(q => q.score_percentage !== undefined)
                    ? `${Math.round(questionnaires.filter(q => q.dataset_type === 'risk').reduce((sum, q) => sum + (q.score_percentage || 0), 0) / Math.max(questionnaires.filter(q => q.dataset_type === 'risk').length, 1))}%`
                    : 'N/A'
                }
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <ApprovalQuestionnaireCards
        questionnaires={questionnaires}
        approvalStatuses={approvalStatuses}
        onViewQuestionnaire={(id) => navigate(`/onboarding/review/${id}`)}
      />

      {/* No questionnaires but pending approval */}
      {questionnaires.length === 0 && supplierOnboardingStatus === 'pending_initial' && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <p className="text-sm text-amber-800">This supplier is awaiting initial approval. Review the details above and approve to proceed with sending the invitation.</p>
        </div>
      )}

      {/* Action Buttons */}
      {hasPendingApprovals && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t">
          <p className="text-xs text-muted-foreground mr-auto hidden sm:block">
            Approving will create an invitation and assign the default onboarding questionnaire.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(`/suppliers/wizard/${supplierId}`)}
          >
            Open supplier wizard
          </Button>
          <Button
            variant="outline"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={() => setShowMoreInfoModal(true)}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            More Information
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeclineModal(true)}
          >
            <X className="h-4 w-4 mr-2" />
            Reject
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setShowApproveModal(true)}
          >
            <Check className="h-4 w-4 mr-2" />
            Approve & Send Invitation
          </Button>
        </div>
      )}
      {!hasPendingApprovals && (
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => navigate(`/suppliers/${supplierId}`)}>
            Go To Supplier Details
          </Button>
        </div>
      )}

      <ApprovalModals
        showDeclineModal={showDeclineModal}
        setShowDeclineModal={setShowDeclineModal}
        declineReason={declineReason}
        setDeclineReason={setDeclineReason}
        handleDecline={handleDecline}
        actionLoading={actionLoading}
        showMoreInfoModal={showMoreInfoModal}
        setShowMoreInfoModal={setShowMoreInfoModal}
        moreInfoRequired={moreInfoRequired}
        setMoreInfoRequired={setMoreInfoRequired}
        selectedQuestionnaires={selectedQuestionnaires}
        setSelectedQuestionnaires={setSelectedQuestionnaires}
        questionnaires={questionnaires}
        handleRequestMoreInfo={handleRequestMoreInfo}
        showApproveModal={showApproveModal}
        setShowApproveModal={setShowApproveModal}
        approvalLevel={approvalLevel}
        setApprovalLevel={setApprovalLevel}
        approvalComment={approvalComment}
        setApprovalComment={setApprovalComment}
        handleApprove={handleApprove}
      />
    </div>
  );
}
